import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import PropTypes from 'prop-types';
import InfoBox from './InfoBox';
import './OverviewMap.scss';

const OverviewMap = ({ title, text, areas }) => {
    const [activeArea, setActiveArea] = useState(null);
    const [activeMarker, setActiveMarker] = useState(null);
    const [boxPosition, setBoxPosition] = useState([0, 0]);
    const [boxAlignment, setBoxAlignment] = useState('left');
    const [dimensions, setDimensions] = React.useState({
        height: window.innerHeight,
        width: window.innerWidth,
    });
    const defaultOpacity = 0;
    const activeMakerZoom = dimensions.width > 1150 ? 1 : 1.6;
    const defaultMarkerZoom = dimensions.width > 1150 ? 0.85 : 1.4;

    const { ref, inView } = useInView({
        threshold: 0,
        triggerOnce: true,
    });

    function debounce(fn, ms) {
        let timer;
        return () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                timer = null;
                fn.apply(this, arguments);
            }, ms);
        };
    }

    useEffect(() => {
        const debouncedHandleResize = debounce(function handleResize() {
            setDimensions({
                height: window.innerHeight,
                width: window.innerWidth,
            });
        }, 1000);
        window.addEventListener('resize', debouncedHandleResize);
        return () => {
            window.removeEventListener('resize', debouncedHandleResize);
        };
    });

    const areaStyle = {
        fill: 'none',
        stroke: '#e44881',
        strokeOpacity: 1,
        strokeWidth: dimensions.width > 1150 ? 6 : 12,
        strokeDasharray: 20,
        opacity: defaultOpacity,
        transition: 'opacity 0.3s linear',
        animation: 'areaDash 20s linear infinite',
    };

    const markerStyle = {
        filter: 'drop-shadow(10px -5px 5px rgba(0, 0, 0, 0.3))',
        strokeWidth: 0,
        cursor: 'pointer',
        transition: 'all 0.3s',
    };

    const markerClick = (id, position = '', alignment) => {
        setActiveArea(null);
        setTimeout(() => {
            setActiveArea(id);
            setActiveMarker(id);
        }, 100);
        setBoxPosition(position);
        setBoxAlignment(alignment);
    };

    return (
        <div className="OverviewMap" ref={ref}>
            <h2 className="OverviewMap__Title">{title}</h2>
            {text && <p className="OverviewMap__Text">{text}</p>}
            <div className="OverviewMap__Content">
                <div className="OverviewMap__MapContainer">
                    <div className="OverviewMap__Map" />
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        id="svg2"
                        viewBox="0 0 1680 2077.3333">
                        <g>
                            <path
                                style={{
                                    ...areaStyle,
                                    opacity: activeArea === 0 ? 1 : defaultOpacity,
                                }}
                                d="m 481.8989,439.08918 -46.34822,55.11808 22.28115,363.43774 -7.68198,11.46099 31.00146,12.90084 19.63121,-21.71212 18.28413,21.69257 7.04063,-4.92358 -25.39187,-31.90967 -4.87128,-15.175 33.51732,-7.43238 30.49225,9.15309 8.81988,15.84485 11.10565,-6.34654 14.39494,-25.93687 9.99483,-32.75697 -38.13208,-14.60477 40.51665,-103.05518 106.06139,43.04708 23.40947,-40.15464 67.9666,-34.03483 5.19815,-55.12217 -44.02271,-68.83914 -29.58337,-0.70248 -28.20425,-7.76502 -34.48245,-18.3101 -28.63654,-6.31202 -26.59042,-0.85273 -29.80039,-7.04454 -10.31247,-0.55487 -16.06025,2.02589 -16.65927,-1.20596 -7.0631,-2.15442 z"
                            />
                            <path
                                style={{
                                    ...areaStyle,
                                    opacity: activeArea === 1 ? 1 : defaultOpacity,
                                }}
                                d="m 450.14985,869.10599 -86.08739,109.36185 29.02856,21.88946 10.80543,10.4519 9.59972,10.2984 34.1031,44.052 9.2863,11.3454 15.13718,22.4553 11.53293,-7.831 -1.75311,-18.9667 10.67965,-46.7484 -2.35144,-2.2606 2.92343,-7.7902 16.24616,-18.13024 10.88218,-4.81704 38.58491,-81.89464 23.225,-11.02477 19.69247,8.19829 3.5556,-9.71344 -13.40514,-6.79217 -13.66172,-18.64798 3.19268,-2.25757 -12.69277,-22.73925 -8.81988,-15.84485 -30.49225,-9.15309 -33.51732,7.43238 4.87128,15.175 25.39187,31.90967 -7.04063,4.92358 -18.28413,-21.69257 -19.63121,21.71212 z"
                            />
                            <path
                                style={{
                                    ...areaStyle,
                                    opacity: activeArea === 2 ? 1 : defaultOpacity,
                                }}
                                d="m 503.83238,1200.5245 0.0619,4.3281 5.27107,20.0152 146.86254,-38.3581 22.44568,-4.0521 69.39154,-0.5166 86.75575,-16.1276 6.99409,35.4153 59.03374,-12.4713 -5.98815,-35.3452 43.48755,-8.8086 11.80178,-66.4351 150.93263,24.4302 -6.5979,-72.9406 -17.1729,-53.10751 -123.65459,33.38751 -0.55929,-54.89213 -96.92721,-52.71134 -3.0163,4.14864 -7.31606,-4.75893 39.31594,-70.00715 5.44738,-17.17841 -32.34606,-11.62727 -13.99973,-83.32806 -40.06454,-85.88158 -67.96659,34.03483 -23.40949,40.15464 -106.06138,-43.04708 -40.51664,103.05518 38.13208,14.60477 -9.99483,32.75697 -14.39495,25.93687 -11.10564,6.34654 12.69278,22.73925 -3.19268,2.25757 13.66171,18.64798 13.40514,6.79217 -3.5556,9.71344 -19.69248,-8.19829 -23.22499,11.02477 -38.58491,81.89464 -10.88216,4.81704 -16.24619,18.13024 36.63718,16.3673 10.06895,69.4967 -0.41258,41.2389 7.0079,44.8035 -0.0392,8.1934 -15.09688,10.5591 -8.22087,0.7817 z"
                            />
                            <path
                                style={{
                                    ...areaStyle,
                                    opacity: activeArea === 3 ? 1 : defaultOpacity,
                                }}
                                d="m 855.9704,902.33463 96.92734,52.71134 0.55919,54.89213 123.65457,-33.38751 17.1729,53.10751 6.5979,72.9406 -11.0442,68.7124 148.0631,26.5713 52.0481,6.3364 101.6746,-34.9799 -85.1346,-189.44084 -20.7597,-93.2021 -4.1516,-65.68717 -3.6989,-48.22046 -247.6806,17.14826 -46.5117,-49.32013 -34.97308,7.50207 -29.70206,43.26446 z"
                            />
                            <path
                                style={{
                                    ...areaStyle,
                                    opacity: activeArea === 4 ? 1 : defaultOpacity,
                                }}
                                d="m 834.62064,1165.8134 6.9941,35.4153 43.15455,-9.055 35.17404,78.0352 -11.29758,69.4388 -22.5537,-4.8602 -3.05063,17.8973 -20.35924,-2.0891 -3.66202,20.0005 -129.51569,18.9274 19.95509,210.7643 -2.53711,-0.5163 -5.396,-6.6355 -7.32473,-4.874 -6.14042,1.5763 -7.39853,5.5942 -4.86478,5.5787 -3.38153,1.4913 -6.56039,7.9686 -0.69055,9.5609 -4.49465,9.3752 -5.77118,4.2847 -10.11564,5.1776 -24.0375,15.7017 -26.97512,13.297 -11.5962,7.8649 -4.51361,-0.3659 -4.71068,2.7632 -6.94987,6.7692 -1.61899,6.6956 -2.99737,11.5046 -0.63888,13.5993 -2.25323,6.3087 -10.31997,13.3921 -18.31687,11.6671 -17.94885,4.6356 -5.76244,-1.6968 -4.38284,0.5039 -7.1305,-1.1449 -5.33695,1.4052 -6.10619,0.015 -3.80104,-2.0062 -11.12214,-13.603 -6.39907,-13.6177 -5.66053,-4.9387 -4.39213,-14.1944 -1.15922,-9.9562 -2.86118,-12.0814 -4.63589,-8.3305 -5.69505,-1.5885 19.12792,-102.1724 -28.52797,-152.4053 75.50862,-55.6456 -34.40518,-136.367 146.86255,-38.3584 22.44568,-4.052 69.39155,-0.5166 z"
                            />
                            <path
                                style={{
                                    ...areaStyle,
                                    opacity: activeArea === 5 ? 1 : defaultOpacity,
                                }}
                                d="m 749.45956,1600.2879 3.98181,-0.4168 2.77012,-2.0854 8.89567,1.268 7.01002,-0.1066 16.93825,4.5951 6.00778,-0.8212 11.69622,1.791 2.66506,-2.186 2.85536,-5.666 0.77367,-6.5879 8.63408,0.571 11.30511,6.9549 27.59074,10.2876 12.82504,-1.0218 14.12358,-5.0842 8.82674,-7.9007 13.76185,1.7298 4.83779,5.3436 -0.2107,7.6521 4.41532,8.6915 5.48705,0.9927 2.40458,7.6493 6.19972,1.2095 -0.61703,8.6557 2.91838,5.1337 11.39256,6.7463 6.80574,21.6363 -0.0277,2.0103 2.29316,2.8481 0.15772,4.4978 16.27102,17.7794 15.23394,14.2282 -1.60664,15.0443 0.64555,16.4247 2.03727,6.6769 -0.34685,4.5466 -1.00575,1.7985 1.12157,2.6582 -1.44635,2.5665 3.0918,9.3743 5.33157,1.878 0.6321,3.5671 -2.16471,4.437 3.0494,6.5471 3.38503,18.5935 -0.53884,5.8384 2.94404,6.7918 -0.075,5.9578 5.1144,0.8777 0.7368,8.4899 -1.6501,4.1808 0.7705,9.7088 -0.5276,8.1464 2.8679,5.4324 1.9703,10.895 2.7161,1.7141 2.4429,5.0845 1.8688,1.5846 1.7915,8.6888 5.5849,4.903 9.9614,4.798 12.8568,-0.4689 6.954,-8.8194 22.454,1.0106 10.7208,3.7907 8.342,-0.7433 1.3888,3.9281 5.4299,-5.1933 5.5967,-0.8369 21.2988,-8.7738 7.1341,-5.1279 19.8667,-5.6078 7.767,-0.3143 12.7757,-3.1973 2.2659,4.6565 2.0955,-0.12 4.5678,-6.0734 15.3203,-4.3145 15.7995,-9.3451 9.2256,-12.8923 2.5853,-1.8529 2.9259,-8.6341 3.3768,-2.8265 4.6169,-16.4614 4.5691,-2.0439 3.0681,-24.0551 1.6754,-9.0302 1.2055,-11.4821 3.5252,-13.4315 6.9835,-9.4654 -1.5241,-12.4831 4.934,-20.0035 0.873,-10.8409 -0.9418,-12.5702 0.9547,-4.7256 -4.9842,-9.4047 -1.8665,-7.1074 -1.0576,-8.272 -10.6441,-11.9199 -2.5435,-16.3216 1.5646,-15.8205 -1.9123,-5.2613 1.4551,-27.0663 8.1605,0.7553 30.1683,-8.1965 3.2213,-6.1549 2.8476,2.1804 9.2207,-5.7441 6.7776,-8.0162 2.3261,-8.799 3.4265,-11.8169 3.495,-3.1218 -0.306,-4.4454 4.3266,-8.3734 2.1701,-11.1254 3.2715,-3.2516 -5.4434,-14.4009 -5.2163,-2.4231 -0.1462,-2.9172 3.1185,-2.6868 5.0447,0.3026 21.7157,-6.618 3.1716,-4.0348 4.8608,-2.2832 6.3361,-11.0221 10.1787,-3.8459 4.2427,0.8676 10.8635,-5.1339 9.6569,3.7406 5.5932,-3.8142 0.7361,-6.5355 11.9094,-14.3667 11.9034,-9.5767 6.787,-0.8118 25.976,-95.209 -3.8863,-15.8427 -61.9667,-136.4044 -101.6742,34.9799 -51.9116,-6.2734 -148.1998,-26.6341 11.0442,-68.7127 -150.9327,-24.4302 -11.80182,66.435 -43.48757,8.8086 5.98815,35.3454 -15.87918,3.4163 35.17416,78.0353 -11.29769,69.4383 -22.55371,-4.8596 -3.05051,17.897 -20.35934,-2.0894 -3.66204,20.0007 -129.51569,18.9275 z"
                            />
                            <path
                                style={{
                                    ...areaStyle,
                                    opacity: activeArea === 6 ? 1 : defaultOpacity,
                                }}
                                d="m 493.05421,1015.3634 36.63715,16.3673 10.06898,69.4967 -0.4126,41.2389 7.0079,44.8035 -0.0392,8.1934 -15.09689,10.5591 -8.22086,0.7817 -19.16612,-6.2795 0.53244,-19.9009 -2.72481,-16.8959 -3.88149,-14.4551 -6.22695,-16.7907 -5.45673,-10.9234 -14.05202,-22.5982 11.53286,-7.831 -1.75304,-18.9667 10.67965,-46.7484 -2.35151,-2.2606 z"
                            />
                        </g>
                        <g
                            style={{opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(-50vh)', transition: 'transform 0.4s cubic-bezier(0.175, 0.1, 0.32, 1.275)'}}
                        >
                            <g
                                transform="translate(523.09887,525.50611)">
                                <g
                                    onClick={() => markerClick(0, ['21%', '50%'])}
                                    onMouseOver={() => setActiveMarker(0)}
                                    onMouseOut={() => setActiveMarker(activeArea)}
                                    style={{
                                        ...markerStyle,
                                        transform:
                                        activeMarker === 0
                                            ? `scale(${activeMakerZoom})`
                                            : `scale(${defaultMarkerZoom})`,
                                    }}
                                >
                                    <path
                                        fill="#00797b"
                                        d="M 24.828832,80.56573 C 18.057332,62.494912 0,43.499471 0,31.247456 0,14.116322 15.943119,0 33.059965,0 50.181326,0 60,20.243835 60,37.374969 c 0,6.616931 -7.51185,14.85873 -12.79061,23.341474 -4.484238,7.207244 -7.543451,8.917947 -10.533444,18.070818 -0.887819,2.718152 -2.741705,5.341432 -4.190806,7.113878 -1.106011,1.353806 -3.035136,1.501383 -4.193815,0.192756 -1.092469,-1.23484 -2.40313,-3.053968 -3.460989,-5.526659 z"
                                    />
                                    <path
                                        fill="#99c7c2"
                                        d="m 32.04424,51.146438 c -9.191182,0 -18.692348,-8.753804 -18.692348,-18.048229 0,-9.294423 8.777368,-16.585999 17.967046,-16.585999 2.690542,0 5.712136,1.049613 7.960273,2.195604 4.582048,0.487913 8.682568,8.059584 8.682568,14.63435 0,9.295931 -6.726357,17.805781 -15.917539,17.805781 z"
                                    />
                                </g>
                            </g>
                            <g
                                transform="translate(468.73828,809.14676)"
                            >
                                <g
                                    onClick={() => markerClick(1, ['37%', '37%'])}
                                    onMouseOver={() => setActiveMarker(1)}
                                    onMouseOut={() => setActiveMarker(activeArea)}
                                    style={{
                                        ...markerStyle,
                                        transform:
                                        activeMarker === 1
                                            ? `scale(${activeMakerZoom})`
                                            : `scale(${defaultMarkerZoom})`,
                                    }}
                                >
                                    <path
                                        fill="#00797b"
                                        d="M 24.828832,80.56573 C 18.057332,62.494912 0,43.499471 0,31.247456 0,14.116322 15.943119,0 33.059965,0 50.181326,0 60,20.243835 60,37.374969 c 0,6.616931 -7.51185,14.85873 -12.79061,23.341474 -4.484238,7.207244 -7.543451,8.917947 -10.533444,18.070818 -0.887819,2.718152 -2.741705,5.341432 -4.190806,7.113878 -1.106011,1.353806 -3.035136,1.501383 -4.193815,0.192756 -1.092469,-1.23484 -2.40313,-3.053968 -3.460989,-5.526659 z"
                                    />
                                    <path
                                        fill="#99c7c2"
                                        d="m 32.04424,51.146438 c -9.191182,0 -18.692348,-8.753804 -18.692348,-18.048229 0,-9.294423 8.777368,-16.585999 17.967046,-16.585999 2.690542,0 5.712136,1.049613 7.960273,2.195604 4.582048,0.487913 8.682568,8.059584 8.682568,14.63435 0,9.295931 -6.726357,17.805781 -15.917539,17.805781 z"
                                    />
                                </g>
                            </g>
                            <g
                                transform="translate(684.69152,900.8994)"
                            >
                                <g
                                    onClick={() => markerClick(2, ['37%', '66%'])}
                                    onMouseOver={() => setActiveMarker(2)}
                                    onMouseOut={() => setActiveMarker(activeArea)}
                                    style={{
                                        ...markerStyle,
                                        transform:
                                        activeMarker === 2
                                            ? `scale(${activeMakerZoom})`
                                            : `scale(${defaultMarkerZoom})`,
                                    }}
                                >
                                    <path
                                        fill="#00797b"
                                        d="M 24.828832,80.56573 C 18.057332,62.494912 0,43.499471 0,31.247456 0,14.116322 15.943119,0 33.059965,0 50.181326,0 60,20.243835 60,37.374969 c 0,6.616931 -7.51185,14.85873 -12.79061,23.341474 -4.484238,7.207244 -7.543451,8.917947 -10.533444,18.070818 -0.887819,2.718152 -2.741705,5.341432 -4.190806,7.113878 -1.106011,1.353806 -3.035136,1.501383 -4.193815,0.192756 -1.092469,-1.23484 -2.40313,-3.053968 -3.460989,-5.526659 z"
                                    />
                                    <path
                                        fill="#99c7c2"
                                        d="m 32.04424,51.146438 c -9.191182,0 -18.692348,-8.753804 -18.692348,-18.048229 0,-9.294423 8.777368,-16.585999 17.967046,-16.585999 2.690542,0 5.712136,1.049613 7.960273,2.195604 4.582048,0.487913 8.682568,8.059584 8.682568,14.63435 0,9.295931 -6.726357,17.805781 -15.917539,17.805781 z"
                                    />
                                </g>
                            </g>
                            <g
                                transform="translate(1122.751,958.26696)"
                            >
                                <g
                                    onClick={() => markerClick(3, ['37%', '50%'], 'right')}
                                    onMouseOver={() => setActiveMarker(3)}
                                    onMouseOut={() => setActiveMarker(activeArea)}
                                    style={{
                                        ...markerStyle,
                                        transform:
                                        activeMarker === 3
                                            ? `scale(${activeMakerZoom})`
                                            : `scale(${defaultMarkerZoom})`,
                                    }}
                                >
                                    <path
                                        fill="#00797b"
                                        d="M 24.828832,80.56573 C 18.057332,62.494912 0,43.499471 0,31.247456 0,14.116322 15.943119,0 33.059965,0 50.181326,0 60,20.243835 60,37.374969 c 0,6.616931 -7.51185,14.85873 -12.79061,23.341474 -4.484238,7.207244 -7.543451,8.917947 -10.533444,18.070818 -0.887819,2.718152 -2.741705,5.341432 -4.190806,7.113878 -1.106011,1.353806 -3.035136,1.501383 -4.193815,0.192756 -1.092469,-1.23484 -2.40313,-3.053968 -3.460989,-5.526659 z"
                                    />
                                    <path
                                        fill="#99c7c2"
                                        d="m 32.04424,51.146438 c -9.191182,0 -18.692348,-8.753804 -18.692348,-18.048229 0,-9.294423 8.777368,-16.585999 17.967046,-16.585999 2.690542,0 5.712136,1.049613 7.960273,2.195604 4.582048,0.487913 8.682568,8.059584 8.682568,14.63435 0,9.295931 -6.726357,17.805781 -15.917539,17.805781 z"
                                    />
                                </g>
                            </g>
                            <g
                                transform="translate(695.78162,1242.7618)"
                            >
                                <g
                                    onClick={() => markerClick(4, ['56%', '58%'])}
                                    onMouseOver={() => setActiveMarker(4)}
                                    onMouseOut={() => setActiveMarker(activeArea)}
                                    style={{
                                        ...markerStyle,
                                        transform:
                                        activeMarker === 4
                                            ? `scale(${activeMakerZoom})`
                                            : `scale(${defaultMarkerZoom})`,
                                    }}
                                >
                                    <path
                                        fill="#00797b"
                                        d="M 24.828832,80.56573 C 18.057332,62.494912 0,43.499471 0,31.247456 0,14.116322 15.943119,0 33.059965,0 50.181326,0 60,20.243835 60,37.374969 c 0,6.616931 -7.51185,14.85873 -12.79061,23.341474 -4.484238,7.207244 -7.543451,8.917947 -10.533444,18.070818 -0.887819,2.718152 -2.741705,5.341432 -4.190806,7.113878 -1.106011,1.353806 -3.035136,1.501383 -4.193815,0.192756 -1.092469,-1.23484 -2.40313,-3.053968 -3.460989,-5.526659 z"
                                        id="path1031-0-2"
                                    />
                                    <path
                                        fill="#99c7c2"
                                        d="m 32.04424,51.146438 c -9.191182,0 -18.692348,-8.753804 -18.692348,-18.048229 0,-9.294423 8.777368,-16.585999 17.967046,-16.585999 2.690542,0 5.712136,1.049613 7.960273,2.195604 4.582048,0.487913 8.682568,8.059584 8.682568,14.63435 0,9.295931 -6.726357,17.805781 -15.917539,17.805781 z"
                                    />
                                </g>
                            </g>
                            <g
                                transform="translate(1032.9019,1259.7067)"
                            >
                                <g
                                    onClick={() => markerClick(5, ['58%', '58%'], 'right')}
                                    onMouseOver={() => setActiveMarker(5)}
                                    onMouseOut={() => setActiveMarker(activeArea)}
                                    style={{
                                        ...markerStyle,
                                        transform:
                                        activeMarker === 5
                                            ? `scale(${activeMakerZoom})`
                                            : `scale(${defaultMarkerZoom})`,
                                    }}
                                >
                                    <path
                                        fill="#00797b"
                                        d="M 24.828832,80.56573 C 18.057332,62.494912 0,43.499471 0,31.247456 0,14.116322 15.943119,0 33.059965,0 50.181326,0 60,20.243835 60,37.374969 c 0,6.616931 -7.51185,14.85873 -12.79061,23.341474 -4.484238,7.207244 -7.543451,8.917947 -10.533444,18.070818 -0.887819,2.718152 -2.741705,5.341432 -4.190806,7.113878 -1.106011,1.353806 -3.035136,1.501383 -4.193815,0.192756 -1.092469,-1.23484 -2.40313,-3.053968 -3.460989,-5.526659 z"
                                    />
                                    <path
                                        fill="#99c7c2"
                                        d="m 32.04424,51.146438 c -9.191182,0 -18.692348,-8.753804 -18.692348,-18.048229 0,-9.294423 8.777368,-16.585999 17.967046,-16.585999 2.690542,0 5.712136,1.049613 7.960273,2.195604 4.582048,0.487913 8.682568,8.059584 8.682568,14.63435 0,9.295931 -6.726357,17.805781 -15.917539,17.805781 z"
                                    />
                                </g>
                            </g>
                        </g>
                    </svg>
                </div>
                {activeArea !== null && (
                    <InfoBox
                        setActiveArea={setActiveArea}
                        position={boxPosition}
                        alignment={boxAlignment}
                        windowWidth={dimensions.width}
                        {...areas[activeArea]}
                    />
                )}
            </div>
        </div>
    );
};

OverviewMap.propTypes = {
    title: PropTypes.string,
    areas: PropTypes.array,
};

OverviewMap.defaultProps = {};

export default OverviewMap;
